import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../lib/auth";
import { Sparkles, Images, Wand2, Crown, LogOut, User } from "lucide-react";
import { Button } from "./ui/button";
import { NotificationBell } from "./NotificationBell";

const NAV = [
  { to: "/studio", label: "Studio", icon: Wand2, testId: "nav-studio" },
  { to: "/gallery", label: "Gallery", icon: Images, testId: "nav-gallery" },
  { to: "/pricing", label: "Plans", icon: Crown, testId: "nav-pricing" },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="relative min-h-screen bg-[#09090B] text-zinc-100">
      {/* Ambient glows */}
      <div className="ambient-glow -top-40 -left-40 w-[420px] h-[420px] opacity-30" style={{ background: "#D946EF" }} />
      <div className="ambient-glow top-1/3 -right-40 w-[420px] h-[420px] opacity-20" style={{ background: "#06B6D4" }} />

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col p-6 border-r border-white/5 z-40 bg-[#09090B]/90 backdrop-blur-xl">
        <Logo />
        <nav className="mt-10 space-y-1 flex-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-white/8 text-white border border-white/10"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 border-t border-white/5 space-y-3">
          {user ? (
            <>
              <div className="flex items-center gap-2">
                <button
                  data-testid="sidebar-profile-btn"
                  onClick={() => navigate("/profile")}
                  className="flex-1 flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors min-w-0"
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white text-sm font-bold shrink-0">
                    {(user.name || user.email)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium truncate">{user.name || user.email.split("@")[0]}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{user.plan} · {user.credits} cr</div>
                  </div>
                </button>
                <NotificationBell />
              </div>
              <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start text-zinc-400 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </>
          ) : (
            <NavLink to="/login" data-testid="sidebar-login" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
              <User className="w-4 h-4" /> Sign in
            </NavLink>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 glass px-4 h-14 flex items-center justify-between">
        <Logo />
        {user ? (
          <div className="flex items-center gap-2">
            <NotificationBell compact />
            <div className="text-[11px] uppercase tracking-widest text-zinc-400">{user.credits} cr</div>
            <button
              data-testid="mobile-profile-btn"
              onClick={() => navigate("/profile")}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white text-xs font-bold"
            >
              {(user.name || user.email)[0]?.toUpperCase()}
            </button>
          </div>
        ) : (
          <NavLink to="/login" data-testid="mobile-login" className="text-sm text-cyan-400">Sign in</NavLink>
        )}
      </header>

      {/* Main content */}
      <main className="md:pl-64 pt-14 md:pt-0 pb-20 md:pb-6 relative z-10">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass-strong border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around px-2">
          {NAV.map((item) => {
            const active = location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`bottom-${item.testId}`}
                className="flex-1 py-3 flex flex-col items-center gap-1 relative"
              >
                <item.icon className={`w-5 h-5 transition-colors ${active ? "text-fuchsia-400" : "text-zinc-500"}`} strokeWidth={2} />
                <span className={`text-[10px] uppercase tracking-widest ${active ? "text-white" : "text-zinc-500"}`}>{item.label}</span>
                {active && <span className="absolute top-1 h-1 w-8 rounded-full bg-fuchsia-400" />}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
